import { useReduxState } from 'hooks';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';

import type { CategoricalChartFunc } from 'recharts/types/chart/types';

interface Props {
  accessibilityLayer?: boolean;
  id: number;
  data: ({ label: string } & { [x: string]: string | number })[];
  height?: number;
  onClick?: CategoricalChartFunc;
  progress?: number;
  xAxisDataKey: string;
  yAxisDataKey: string;
}

const AreaProgressChart = ({
  accessibilityLayer = false,
  id,
  data,
  height = 300,
  onClick,
  progress = 0,
  xAxisDataKey,
  yAxisDataKey
}: Props) => {
  const { theme } = useReduxState();

  return (
    <ResponsiveContainer height={height} width="100%">
      <AreaChart
        accessibilityLayer={accessibilityLayer}
        data={data}
        margin={{ top: 20, right: 10, left: 10, bottom: 10 }}
        onClick={onClick}
      >
        <defs>
          <linearGradient id={`color-${id}`} x1="0" y1="0" x2="1" y2="0">
            <stop
              offset={progress}
              stopColor={theme.chartMainColor}
              stopOpacity={1}
            />

            <stop
              offset={progress}
              stopColor={theme.chartMainColor}
              stopOpacity={0.4}
            />
          </linearGradient>
        </defs>

        <CartesianGrid stroke="#ddd" strokeDasharray="3 3" />

        <XAxis
          dataKey={xAxisDataKey}
          dy={10}
          fontSize={14}
          fontWeight={600}
          stroke="#aaa"
        />

        <YAxis
          allowDecimals={false}
          dx={-10}
          fontSize={14}
          fontWeight={600}
          stroke="#aaa"
          width={30}
        />

        <Area
          activeDot={{
            fill: 'white',
            r: 4,
            stroke: theme.chartMainColor,
            strokeWidth: 2
          }}
          dataKey={yAxisDataKey}
          fill={`url(#color-${id})`}
          stroke={theme.chartMainColor}
          type="basis"
        />

        <Tooltip
          animationDuration={200}
          contentStyle={{
            border: 0,
            borderRadius: 4,
            boxShadow: '0 1px 5px 0 rgba(0, 0, 0, 0.2)'
          }}
          itemStyle={{ color: theme.textColor }}
          labelStyle={{ color: '#999' }}
          wrapperStyle={{
            border: 0,
            borderRadius: 3,
            fontSize: '1.4rem',
            fontWeight: 600
          }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default AreaProgressChart;
